import { Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router';
import { Button } from '@/components/ui/button';
import { GetStartedLink, PrimaryNavigation } from '@/constants/navigation';
import { cn } from '@/lib/utils';

const DesktopMinWidthPx = 1024;
const MobileNavigationId = 'mobile-navigation';

function getMobileLinkClassName({ isActive }) {
  return cn(
    'rounded-md px-3 py-3 text-lg font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-graphite',
    isActive ? 'bg-pad text-graphite shadow-[inset_3px_0_0_var(--color-redline)]' : 'text-steel hover:bg-pad hover:text-graphite'
  );
}

export function MobileMenu() {
  const menuButtonRef = useRef(null);
  const menuPanelRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(event) {
      const isInsidePanel = menuPanelRef.current?.contains(event.target);
      const isOnMenuButton = menuButtonRef.current?.contains(event.target);
      if (!isInsidePanel && !isOnMenuButton) setIsOpen(false);
    }

    function handleKeyDown(event) {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      menuButtonRef.current?.focus();
    }

    function handleResize() {
      if (window.innerWidth >= DesktopMinWidthPx) setIsOpen(false);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  function toggleMenu() {
    setIsOpen((wasOpen) => !wasOpen);
  }

  function closeMenu() {
    setIsOpen(false);
  }

  return (
    <>
      <Button
        ref={menuButtonRef}
        variant="ghost"
        size="icon"
        aria-controls={MobileNavigationId}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        onClick={toggleMenu}
        className="lg:hidden"
      >
        {isOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>
      {isOpen ? <div aria-hidden="true" className="fixed inset-x-0 bottom-0 top-[calc(4rem_+_1px)] z-30 bg-graphite/20 lg:hidden" /> : null}
      <div
        ref={menuPanelRef}
        id={MobileNavigationId}
        inert={!isOpen}
        className={cn(
          'absolute inset-x-0 top-full z-40 overflow-hidden bg-paper motion-safe:transition-[max-height,opacity] motion-safe:duration-200 lg:hidden',
          isOpen ? 'max-h-96 border-b border-grid opacity-100 shadow-[0_16px_32px_-24px_rgb(43_48_44/0.5)]' : 'pointer-events-none max-h-0 opacity-0'
        )}
      >
        <nav aria-label="Mobile" className="section-container flex flex-col gap-1 py-4">
          {PrimaryNavigation.map((navigationLink) => (
            <NavLink key={navigationLink.to} to={navigationLink.to} end onClick={closeMenu} className={getMobileLinkClassName}>
              {navigationLink.label}
            </NavLink>
          ))}
          <Button asChild className="mt-3 w-full sm:hidden">
            <Link to={GetStartedLink.to} onClick={closeMenu}>
              {GetStartedLink.label}
            </Link>
          </Button>
        </nav>
      </div>
    </>
  );
}